import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Screen } from '@/components/layout/Screen';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bird, Info, Sun, MapPin, ZoomIn, ZoomOut, Thermometer, BarChart3 } from 'lucide-react';
import {
  EnvironmentMonitoring,
  CropMonitoring,
  WeatherOverlay,
  YieldPrediction,
  FieldData,
  NDVIData,
  satelliteApi,
} from '@/features/satellite';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';

// Satellite map component with Dhaka demo
const SatelliteMapView: React.FC<{ field: FieldData; locationLabel?: string; capacity?: number | null; batchDay?: number | null; aliveCount?: number | null }> = ({ field, locationLabel, capacity, batchDay, aliveCount }) => {
  const { t } = useTranslation();
  const [mapType, setMapType] = useState<'satellite' | 'ndvi' | 'thermal'>('satellite');
  const [zoomLevel, setZoomLevel] = useState(15);

  const mapTypes = [
    { id: 'satellite', label: t("satellite.satellite", { defaultValue: "Satellite" }), color: 'bg-blue-500' },
    { id: 'ndvi', label: t("satellite.environment", { defaultValue: "Environment" }), color: 'bg-green-500' },
    { id: 'thermal', label: t("satellite.thermal", { defaultValue: "Thermal" }), color: 'bg-orange-500' },
  ];

  return (
    <Card className="overflow-hidden">
      {/* Map Container */}
      <div className="relative h-72 bg-muted">
        {/* Demo Satellite Image of Dhaka */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://mt1.google.com/vt/lyrs=s&x=1515&y=860&z=11')`,
            filter: mapType === 'ndvi' ? 'hue-rotate(90deg) saturate(1.5)' : 
                   mapType === 'thermal' ? 'hue-rotate(-30deg) saturate(1.2) brightness(1.1)' : 'none'
          }}
        />
        
        {/* Map Overlay Grid */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="w-full h-full opacity-20">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Location Pin */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full">
          <div className="relative">
            <MapPin className="w-10 h-10 text-destructive drop-shadow-lg" fill="currentColor" />
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-destructive rounded-full animate-ping" />
          </div>
        </div>

        {/* Farm Boundary Overlay */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-24 border-2 border-primary border-dashed rounded-lg opacity-70" />

        {/* Zoom Controls */}
        <div className="absolute right-3 top-3 flex flex-col gap-1">
          <Button 
            size="icon" 
            variant="secondary" 
            className="w-8 h-8"
            onClick={() => setZoomLevel(z => Math.min(z + 1, 20))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button 
            size="icon" 
            variant="secondary" 
            className="w-8 h-8"
            onClick={() => setZoomLevel(z => Math.max(z - 1, 10))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Map Type Selector */}
        <div className="absolute left-3 top-3 flex gap-1">
          {mapTypes.map((type) => (
            <Button
              key={type.id}
              size="sm"
              variant={mapType === type.id ? 'default' : 'secondary'}
              className="text-xs h-7 px-2"
              onClick={() => setMapType(type.id as typeof mapType)}
            >
              {type.label}
            </Button>
          ))}
        </div>

        {/* Coordinates Display */}
        <div className="absolute bottom-3 left-3 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-1.5">
          <p className="text-xs font-mono text-foreground">
            {field.lat.toFixed(4)}°N, {field.lon.toFixed(4)}°E
          </p>
        </div>

        {/* Zoom Level */}
        <div className="absolute bottom-3 right-3 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-1.5">
          <p className="text-xs font-mono text-foreground">
            {t("satellite.zoom", { defaultValue: "Zoom" })}: {zoomLevel}x
          </p>
        </div>
      </div>

      {/* Location Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Bird className="w-4 h-4 text-primary" />
              {field.name}
            </h3>
            <p className="text-sm text-muted-foreground">{locationLabel ?? t("satellite.farmLocation", { defaultValue: "Farm Location" })}</p>
          </div>
          <Badge variant="success">{t("satellite.active", { defaultValue: "Active" })}</Badge>
        </div>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-2 bg-muted/50 rounded-lg">
            <p className="text-lg font-bold text-foreground">{capacity?.toLocaleString() ?? '—'}</p>
            <p className="text-xs text-muted-foreground">{t("satellite.capacity", { defaultValue: "Capacity" })}</p>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <p className="text-lg font-bold text-primary">{batchDay != null ? `${t("cbs.batch.day", { defaultValue: "Day" })} ${batchDay}` : '—'}</p>
            <p className="text-xs text-muted-foreground">{t("satellite.batchDay", { defaultValue: "Batch Day" })}</p>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <p className="text-lg font-bold text-success">{aliveCount?.toLocaleString() ?? '—'}</p>
            <p className="text-xs text-muted-foreground">{t("satellite.alive", { defaultValue: "Alive" })}</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const SatelliteScreen: React.FC = () => {
  const { t } = useTranslation();
  const { data: farmData } = useQuery({
    queryKey: ['farm', 'satellite'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const userId = getCurrentUserId();
      if (!userId) return null;

      const { data, error } = await client
        .from('farms')
        .select('id, name, gps_lat, gps_lng, size_hectares, region, village')
        .eq('user_id', userId)
        .eq('is_active', true)
        .limit(1)
        .single();
      if (error) return null;
      return data;
    },
  });

  const { data: batchData } = useQuery({
    queryKey: ['active-batch', 'satellite'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const userId = getCurrentUserId();
      if (!userId) return null;

      const { data, error } = await client
        .from('batches')
        .select('id, batch_code, breed_id, doc_count, placement_date, current_production_stage, cumulative_feed_kg, cumulative_mortality, current_fcr, latest_avg_weight_kg, current_bird_count, organization_id')
        .eq('user_id', userId)
        .eq('batch_status', 'active')
        .order('placement_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return data;
    },
  });

  const batchDay = batchData?.placement_date
    ? Math.floor((Date.now() - new Date(batchData.placement_date).getTime()) / 86400000) + 1
    : null;

  const aliveCount = batchData
    ? (batchData.doc_count ?? 0) - (batchData.cumulative_mortality ?? 0)
    : null;

  const buildField = (farm: typeof farmData): FieldData => ({
    id: farm?.id ?? 'no-farm',
    name: farm?.name ?? t("satellite.myFarm", { defaultValue: "My Farm" }),
    lat: farm?.gps_lat ? Number(farm.gps_lat) : 23.9036,
    lon: farm?.gps_lng ? Number(farm.gps_lng) : 90.4257,
    area: farm?.size_hectares ? Number(farm.size_hectares) : 0.5,
    cropType: t("satellite.poultryFarm", { defaultValue: "Poultry Farm" }),
    plantingDate: batchData?.placement_date ?? new Date().toISOString().split('T')[0],
  });

  const [field, setField] = useState<FieldData>(buildField(null));
  const [ndviData, setNdviData] = useState<NDVIData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setField(buildField(farmData));
  }, [farmData, batchData]);

  // Fetch NDVI data on mount
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await satelliteApi.fetchNDVIData(
          field,
          new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          new Date().toISOString().split('T')[0]
        );
        setNdviData(data);
      } catch (error) {
        console.error('Failed to fetch NDVI data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [field.id]);

  const handleFieldUpdate = (updates: Partial<FieldData>) => {
    setField({ ...field, ...updates });
  };

  return (
    <Screen
      showTopBar
      showBottomNav
      topBarProps={{ title: t("satellite.farmLocation", { defaultValue: "Farm Location" }), showBack: true }}
    >
      <div className="space-y-4 p-4 pb-20">
        {/* Satellite Map View */}
        <SatelliteMapView field={field} locationLabel={farmData?.village ?? farmData?.region ?? undefined} capacity={batchData?.doc_count} batchDay={batchDay} aliveCount={aliveCount} />

        {/* Info Banner */}
        <Alert>
          <Bird className="h-4 w-4" />
          <AlertDescription>
            {t("satellite.monitorDesc", { defaultValue: "Monitor your farm location with satellite imagery. View temperature, vegetation, and environmental data." })}
          </AlertDescription>
        </Alert>

        {/* Content Tabs */}
        <Tabs defaultValue="ndvi" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="ndvi" className="gap-1 text-xs">
              <Thermometer className="h-4 w-4" />
              {t("satellite.environment", { defaultValue: "Environment" })}
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="w-12 h-12 p-0">
              <Bird className="h-5 w-5" />
            </TabsTrigger>
            <TabsTrigger value="weather" className="w-12 h-12 p-0">
              <Sun className="h-5 w-5" />
            </TabsTrigger>
            <TabsTrigger value="yield" className="w-12 h-12 p-0">
              <BarChart3 className="h-5 w-5" />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ndvi" className="space-y-4 mt-4">
            <EnvironmentMonitoring
              batchId={batchData?.id ?? null}
              batchDay={batchDay}
            />
          </TabsContent>

          <TabsContent value="monitoring" className="space-y-4 mt-4">
            <CropMonitoring field={field} onFieldUpdate={handleFieldUpdate} />
          </TabsContent>

          <TabsContent value="weather" className="space-y-4 mt-4">
            <WeatherOverlay field={field} />
          </TabsContent>

          <TabsContent value="yield" className="space-y-4 mt-4">
            {ndviData.length > 0 ? (
              <YieldPrediction field={field} ndviData={ndviData} />
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t("satellite.loadingEnvironment", { defaultValue: "Loading environmental data..." })}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>

        {/* API Info */}
        <Alert variant="default" className="mt-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="text-sm">
                {t("satellite.imageryDesc", { defaultValue: "Satellite imagery for your farm region. Data updates weekly." })}
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </Screen>
  );
};
